import { Image, ListStyle } from '@yleisradio/areena-types';
import { ApplicationResolution } from '../applicationResolution';
import { getAssetUrl } from '../utils/cloudinary';
import { isClient } from '../../common/utils';

export const imageCache = new Map<string | undefined, HTMLImageElement>();

interface ImageParameters {
  fit?: boolean;
  width?: number;
  height: number;
  aspectRatio?: number;
  blur?: number;
  format?: string;
  quality?: string;
}
const DEFAULT_IMAGE_VARIATION = 'medium_16_9';

export const imageVariations = {
  EXTRA_LARGE_16_9: 'extra_large_16_9',
  EXTRA_LARGE_1_1: 'extra_large_1_1',
  EXTRA_LARGE_LOGO: 'extra_large_logo',
  EXTRA_LARGE_BLURRED: 'extra_large_blurred',
  MEDIUM_16_9: 'medium_16_9',
  MEDIUM_9_13: 'medium_9_13',
  MEDIUM_1_1: 'medium_1_1',
  LIMELIGHT_1_1: 'limelight_1_1',
  LIMELIGHT_LOGO: 'limelight_logo',
  HIGHLIGHT_CARD: 'highlightCard',
  ITEM_LOGO: 'item_logo',
  ITEM_1_1: 'item_1_1',
} as const;

type ImageVariation = (typeof imageVariations)[keyof typeof imageVariations];

const imageParameters: {
  [resolution in ApplicationResolution]: {
    [key in ImageVariation]?: ImageParameters;
  };
} = {
  [ApplicationResolution.FULLHD]: {
    extra_large_16_9: {
      width: 1920,
      height: 1080,
      quality: 'auto:eco',
    },
    extra_large_1_1: {
      height: 510,
      aspectRatio: 1 / 1,
    },
    extra_large_logo: {
      fit: true,
      width: 334,
      height: 184,
      format: 'png',
    },
    extra_large_blurred: {
      width: 960,
      height: 540,
      blur: 2000,
    },
    medium_16_9: {
      height: 216,
      aspectRatio: 16 / 9,
    },
    medium_9_13: {
      height: 381,
      aspectRatio: 9 / 13,
    },
    medium_1_1: {
      height: 240,
      aspectRatio: 1 / 1,
    },
    limelight_1_1: {
      height: 660,
      aspectRatio: 1 / 1,
    },
    limelight_logo: {
      fit: true,
      width: 300,
      height: 120,
      format: 'png',
    },
    highlightCard: {
      width: 824,
      height: 464,
    },
    item_logo: {
      fit: true,
      width: 334,
      height: 184,
      format: 'png',
    },
    item_1_1: {
      width: 660,
      height: 660,
    },
  },
  [ApplicationResolution.HD]: {
    extra_large_16_9: {
      width: 1280,
      height: 720,
      quality: 'auto:eco',
    },
    extra_large_blurred: {
      width: 640,
      height: 360,
      blur: 2000,
    },
    medium_9_13: {
      height: 254,
      aspectRatio: 9 / 13,
    },
    medium_16_9: {
      height: 144,
      aspectRatio: 16 / 9,
    },
    medium_1_1: {
      height: 160,
      aspectRatio: 1 / 1,
    },
    limelight_1_1: {
      height: 440,
      aspectRatio: 1 / 1,
    },
    extra_large_1_1: {
      height: 340,
      aspectRatio: 1 / 1,
    },
    extra_large_logo: {
      fit: true,
      width: 222,
      height: 122,
      format: 'png',
    },
    limelight_logo: {
      fit: true,
      width: 200,
      height: 80,
      format: 'png',
    },
    highlightCard: {
      fit: true,
      width: 527,
      height: 297,
    },
    item_logo: {
      fit: true,
      width: 222,
      height: 123,
      format: 'png',
    },
    item_1_1: {
      width: 440,
      height: 440,
    },
  },
};

const getMediumVariation = (listStyle: ListStyle): ImageVariation => {
  const image = listStyle.image;

  switch (image) {
    case '16:9':
      return 'medium_16_9';
    case '9:13':
      return 'medium_9_13';
    case '1:1':
      return 'medium_1_1';
  }

  return DEFAULT_IMAGE_VARIATION;
};

const getExtraLargeVariation = (listStyle: ListStyle): ImageVariation => {
  const image = listStyle.image;

  switch (image) {
    case '16:9':
      return 'extra_large_16_9';
    case '1:1':
      return 'extra_large_1_1';
  }

  return 'extra_large_16_9';
};

export const getImageVariation = (
  listStyle: ListStyle | undefined
): ImageVariation => {
  if (listStyle === undefined) return DEFAULT_IMAGE_VARIATION;

  const size = listStyle.size;
  switch (size) {
    case 'medium':
      return getMediumVariation(listStyle);
    case 'extra-large':
      return getExtraLargeVariation(listStyle);
  }

  return DEFAULT_IMAGE_VARIATION;
};

type GetUrlAndCacheParams = {
  image: Image | string | undefined;
  applicationResolution: ApplicationResolution;
  imageVariation: ImageVariation;
};
export const getUrlAndCache = ({
  image,
  applicationResolution,
  imageVariation,
}: GetUrlAndCacheParams): string | undefined => {
  const imageParams =
    imageParameters[applicationResolution][imageVariation] ||
    imageParameters[applicationResolution][DEFAULT_IMAGE_VARIATION];

  if (image && imageParams) {
    const url =
      typeof image === 'string' ? image : getAssetUrl(image, imageParams);
    if (isClient()) {
      const cachedImage = window.document.createElement('img');
      imageCache.set(url, cachedImage);
      cachedImage.src = url;

      if (imageCache.size > 200) {
        const oldestEntry = imageCache.keys().next();

        imageCache.delete(oldestEntry.value);
      }
    }

    return url;
  }

  return undefined;
};

type GetImageUrlParams = {
  image: Image | undefined;
  listStyle: ListStyle | undefined;
  applicationResolution: ApplicationResolution;
};

export const getImageUrl = ({
  image,
  listStyle,
  applicationResolution,
}: GetImageUrlParams): string | undefined => {
  const imageVariation = getImageVariation(listStyle);

  return getUrlAndCache({ image, applicationResolution, imageVariation });
};
